import React from 'react';
import { Link } from 'react-router-dom';

type EntityRef =
  | string
  | { _id?: string; id?: string; name?: string; phone?: string; loginId?: string }
  | null
  | undefined;

function resolveId(ref: EntityRef): string | null {
  if (!ref) return null;
  if (typeof ref === 'string') return ref || null;
  return ref._id || ref.id || null;
}

function resolveLabel(
  ref: EntityRef,
  fallback?: string,
  prefer: 'name' | 'phone' = 'name',
): string {
  if (!ref) return fallback || '—';
  if (typeof ref === 'string') return fallback || ref.slice(-6) || '—';
  const name = String(ref.name || '').trim();
  const phone = String(ref.phone || '').trim();
  const loginId = String(ref.loginId || '').trim();
  if (prefer === 'phone') return phone || name || loginId || fallback || '—';
  return name || phone || loginId || fallback || '—';
}

type LinkProps = {
  id?: EntityRef;
  /** Populated object or id — preferred over `id` when both passed */
  entity?: EntityRef;
  label?: string;
  secondary?: string;
  className?: string;
  stopPropagation?: boolean;
  children?: React.ReactNode;
};

function EntityAnchor({
  to,
  className,
  stopPropagation,
  children,
  title,
}: {
  to: string;
  className?: string;
  stopPropagation?: boolean;
  children: React.ReactNode;
  title?: string;
}) {
  return (
    <Link
      to={to}
      title={title}
      className={
        className ||
        'text-primary-600 font-semibold hover:underline hover:text-primary-700'
      }
      onClick={(e) => {
        if (stopPropagation) e.stopPropagation();
      }}
    >
      {children}
    </Link>
  );
}

/** Clickable link to Admin → User details */
export function UserLink({
  id,
  entity,
  label,
  secondary,
  className,
  stopPropagation = true,
  children,
}: LinkProps) {
  const ref = entity ?? id;
  const userId = resolveId(ref);
  if (!userId) {
    return <span className={className || 'text-gray-500'}>{children || label || '—'}</span>;
  }
  const text = children || label || resolveLabel(ref);
  return (
    <EntityAnchor
      to={`/users/${userId}`}
      className={className}
      stopPropagation={stopPropagation}
      title="Open user details"
    >
      {text}
      {secondary ? (
        <span className="block text-[11px] font-normal text-gray-400 no-underline">{secondary}</span>
      ) : null}
    </EntityAnchor>
  );
}

/** Clickable link to Admin → Astrologer details */
export function AstrologerLink({
  id,
  entity,
  label,
  secondary,
  className,
  stopPropagation = true,
  children,
}: LinkProps) {
  const ref = entity ?? id;
  const astroId = resolveId(ref);
  if (!astroId) {
    return <span className={className || 'text-gray-500'}>{children || label || '—'}</span>;
  }
  const text = children || label || resolveLabel(ref);
  return (
    <EntityAnchor
      to={`/astrologers/${astroId}`}
      className={className}
      stopPropagation={stopPropagation}
      title="Open astrologer details"
    >
      {text}
      {secondary ? (
        <span className="block text-[11px] font-normal text-gray-400 no-underline">{secondary}</span>
      ) : null}
    </EntityAnchor>
  );
}

/** Compact name + phone stack used in tables */
export function UserCell({
  entity,
  className,
}: {
  entity: EntityRef;
  className?: string;
}) {
  const id = resolveId(entity);
  const name = resolveLabel(entity, 'User');
  const phone =
    entity && typeof entity === 'object' ? String(entity.phone || '').trim() : '';
  if (!id) {
    return (
      <div className={className}>
        <div className="text-xs text-gray-500">{name}</div>
        {phone ? <div className="text-[11px] text-gray-400">{phone}</div> : null}
      </div>
    );
  }
  return (
    <UserLink entity={entity} stopPropagation className={className || 'block'}>
      <span className="text-xs font-semibold text-primary-600 hover:underline">{name}</span>
      {phone ? <span className="block text-[11px] font-normal text-gray-400">{phone}</span> : null}
    </UserLink>
  );
}

export function AstrologerCell({
  entity,
  className,
}: {
  entity: EntityRef;
  className?: string;
}) {
  const id = resolveId(entity);
  const name = resolveLabel(entity, 'Astrologer');
  const phone =
    entity && typeof entity === 'object' ? String(entity.phone || '').trim() : '';
  if (!id) {
    return (
      <div className={className}>
        <div className="text-xs text-gray-500">{name}</div>
        {phone ? <div className="text-[11px] text-gray-400">{phone}</div> : null}
      </div>
    );
  }
  return (
    <AstrologerLink entity={entity} stopPropagation className={className || 'block'}>
      <span className="text-xs font-semibold text-primary-600 hover:underline">{name}</span>
      {phone ? <span className="block text-[11px] font-normal text-gray-400">{phone}</span> : null}
    </AstrologerLink>
  );
}
